import type { ShallowRef } from 'vue'

import type { ContextMessage } from '../types/chat'
import type { AttentionContextRef } from './velis-attention'
import type {
  BrowserSlot,
  BrowserUpdate,
  PerceptionAssembly,
  PerceptionPermission,
  PerceptionPermissions,
  PerceptionTrace,
  ScreenReadResult,
  ScreenSlot,
} from './velis-perception'

import { shallowRef } from 'vue'

import { attentionEventFromBrowserUpdate, attentionEventFromScreenSlot } from './velis-attention'
import { submitAttentionEvent } from './velis-attention-session'
import { collectPerceptionForTurn, readHeldContext, replaceBrowserSlot } from './velis-perception'

interface PerceptionRuntime {
  browser?: BrowserSlot
  screen?: ScreenSlot
  permissions: ShallowRef<PerceptionPermissions>
  traces: ShallowRef<PerceptionTrace[]>
  screenReader?: { read: () => Promise<ScreenReadResult> }
  pending: PerceptionAssembly[]
}

const browserPermissionKey = 'velis.perception.browser'
const screenPermissionKey = 'velis.perception.screen'

function storage() {
  const candidate = (globalThis as { localStorage?: Storage }).localStorage
  if (!candidate || typeof candidate.getItem !== 'function')
    return undefined
  return candidate
}

function readPermission(key: string): PerceptionPermission {
  return storage()?.getItem(key) === 'off' ? 'off' : 'on-demand'
}

function readStoredPermissions(): PerceptionPermissions {
  return {
    browser: readPermission(browserPermissionKey),
    screen: readPermission(screenPermissionKey),
  }
}

function runtime(): PerceptionRuntime {
  const host = globalThis as typeof globalThis & { __velisPerception?: PerceptionRuntime }
  host.__velisPerception ??= {
    permissions: shallowRef(readStoredPermissions()),
    traces: shallowRef<PerceptionTrace[]>([]),
    pending: [],
  }
  return host.__velisPerception
}

export function perceptionPermissionState() {
  return runtime().permissions
}

export function perceptionTraceState() {
  return runtime().traces
}

export function setPerceptionPermission(kind: 'browser' | 'screen', permission: PerceptionPermission) {
  const current = runtime()
  current.permissions.value = { ...current.permissions.value, [kind]: permission }
  if (permission === 'off' && kind === 'browser')
    current.browser = undefined
  if (permission === 'off' && kind === 'screen')
    current.screen = undefined
  const key = kind === 'browser' ? browserPermissionKey : screenPermissionKey
  if (permission === 'off')
    storage()?.setItem(key, 'off')
  else
    storage()?.removeItem(key)
}

/** On-demand read of the slots already held for this renderer. */
export function readCurrentHeldContext(now = Date.now()) {
  const current = runtime()
  return readHeldContext({
    now,
    permissions: current.permissions.value,
    browser: current.browser,
    screen: current.screen,
  })
}

/** Context slots the latest attention decision can cite. */
export function perceptionContextRefs(): AttentionContextRef[] {
  const current = runtime()
  const refs: AttentionContextRef[] = []
  if (current.browser?.summary)
    refs.push({ slot: 'velis:browser', summary: current.browser.summary })
  if (current.screen?.summary)
    refs.push({ slot: 'velis:screen', summary: current.screen.summary })
  return refs
}

export function acceptBrowserContextUpdate(update: BrowserUpdate) {
  const current = runtime()
  current.browser = replaceBrowserSlot(current.browser, update, current.permissions.value.browser)
  submitAttentionEvent(attentionEventFromBrowserUpdate(update), perceptionContextRefs(), update.capturedAt)
}

export function configureVelisScreenReader(reader: { read: () => Promise<ScreenReadResult> } | undefined) {
  runtime().screenReader = reader
}

export function resetPerceptionSession() {
  const current = runtime()
  current.browser = undefined
  current.screen = undefined
  current.pending = []
  current.screenReader = undefined
  current.permissions.value = { browser: 'on-demand', screen: 'on-demand' }
  current.traces.value = []
  storage()?.removeItem(browserPermissionKey)
  storage()?.removeItem(screenPermissionKey)
}

function rememberTraces(traces: readonly PerceptionTrace[]) {
  if (traces.length === 0)
    return
  const current = runtime()
  current.traces.value = [...traces, ...current.traces.value].slice(0, 20)
}

/**
 * Prepares one perception turn before chat composition.
 * A failure here records a trace and still leaves a turn for the chat runtime.
 */
export async function queuePerceptionTurn(message: string, now = Date.now()) {
  const current = runtime()
  try {
    const assembly = await collectPerceptionForTurn({
      message,
      now,
      permissions: current.permissions.value,
      browser: current.browser,
      screen: current.screen,
      readScreen: current.screenReader?.read,
    })
    current.browser = assembly.browser
    current.screen = assembly.screen
    const screenTrace = assembly.traces.find(trace => trace.kind === 'screen' && trace.injected)
    if (assembly.screen && screenTrace)
      submitAttentionEvent(attentionEventFromScreenSlot(assembly.screen), perceptionContextRefs(), now)
    rememberTraces(assembly.traces)
    current.pending.push(assembly)
  }
  catch (error) {
    console.error('Velis perception failed.', error)
    current.pending.push({ messages: [], traces: [], armedKeys: [] })
  }
}

export function takeQueuedPerceptionContext(context: {
  ingestContextMessage: (message: ContextMessage) => void
  setArmedPerceptionKeys: (keys: readonly string[]) => void
}): ContextMessage | undefined {
  const pending = runtime().pending
  const assembly = pending.shift()
  context.setArmedPerceptionKeys(assembly?.armedKeys ?? [])
  const [first, ...rest] = assembly?.messages ?? []
  for (const message of rest)
    context.ingestContextMessage(message)
  return first
}
